import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, EMPTY, timer, Subject, of } from 'rxjs';
import { catchError, retry, share, switchMap, tap } from 'rxjs/operators';
import {
  webSocket,
  WebSocketSubject,
  WebSocketSubjectConfig,
} from 'rxjs/webSocket';
import { IWebSocketMessage } from '../../models/websocket';
import { toSignal } from '@angular/core/rxjs-interop';

const RECONNECT_INTERVAL_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket$?: WebSocketSubject<IWebSocketMessage<unknown>>;
  private messages$: Observable<IWebSocketMessage<unknown>>;
  private readonly url$ = new BehaviorSubject<string | null>(null);

  private readonly connectionStatus = new BehaviorSubject<boolean>(false);
  public readonly connectionStatus$ = this.connectionStatus.asObservable();
  public readonly connectionStatusSignal = toSignal(this.connectionStatus$, {
    initialValue: false,
  });

  constructor() {
    this.messages$ = this.url$.pipe(
      switchMap((url) => {
        if (!url) {
          this.connectionStatus.next(false);
          return EMPTY;
        }

        if (this.socket$) {
          this.socket$.complete();
        }

        const config: WebSocketSubjectConfig<IWebSocketMessage<unknown>> = {
          url,
          openObserver: {
            next: () => this.connectionStatus.next(true),
          },
          closeObserver: {
            next: () => this.connectionStatus.next(false),
          },
        };
        this.socket$ = webSocket(config);

        return this.socket$.pipe(
          retry({
            delay: (error, retryCount) => {
              console.warn(
                `[WebSocketService] Connection lost to ${url}. Retrying... (Attempt ${retryCount})`,
              );
              this.connectionStatus.next(false);
              return timer(RECONNECT_INTERVAL_MS);
            },
          }),
          catchError((error) => {
            console.error(
              `[WebSocketService] Unrecoverable error for ${url}.`,
              error,
            );
            return EMPTY;
          }),
        );
      }),
      share(),
    );
  }

  public connect(url: string): Observable<IWebSocketMessage<unknown>> {
    this.url$.next(url);
    return this.messages$;
  }

  public sendMessage(message: Partial<IWebSocketMessage<unknown>>): void {
    if (!this.socket$ || !this.connectionStatus.value) {
      console.warn(
        '[WebSocketService] Cannot send message while not connected.',
      );
      return;
    }

    const messageToSend: IWebSocketMessage<unknown> = {
      ...message,
      timestamp: new Date().toISOString(),
    } as IWebSocketMessage<unknown>;
    this.socket$.next(messageToSend);
  }

  public disconnect(): void {
    this.url$.next(null);
    this.socket$?.complete();
    this.socket$ = undefined;
  }
}
